import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, ArrowRight, Copy, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderConfirmationPage() {
  const { t } = useLanguage();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID').format(price);
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
        toast.error('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  const copyOrderNumber = () => {
    if (order?.order_number) {
      navigator.clipboard.writeText(order.order_number);
      toast.success('Order number copied!');
    }
  };

  const getPaymentInstructions = () => {
    if (!order) return null;

    if (order.payment.type === 'qris') {
      return (
        <div className="bg-purple-50 rounded-xl p-6 text-center">
          <div className="w-48 h-48 bg-white rounded-xl mx-auto mb-4 flex items-center justify-center border-2 border-purple-200">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">QRIS Code</p>
              <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-4xl">📱</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-purple-700">
            Scan QR code menggunakan aplikasi pembayaran Anda
          </p>
        </div>
      );
    }

    if (order.payment.type === 'bank_transfer') {
      const bankNames = {
        bca: 'Bank BCA',
        bni: 'Bank BNI',
        bri: 'Bank BRI',
        mandiri: 'Bank Mandiri',
        ina: 'Bank INA',
        permata: 'Bank Permata'
      };

      return (
        <div className="bg-blue-50 rounded-xl p-6">
          <h4 className="font-medium text-gray-900 mb-4">Transfer ke:</h4>
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-gray-500 mb-1">{bankNames[order.payment.bank_code] || 'Bank'}</p>
            <p className="text-lg font-mono font-bold text-gray-900">8277 0812 3456 7890</p>
            <p className="text-sm text-gray-500 mt-1">a.n. GIZI.AI MARKETPLACE</p>
          </div>
          <div className="mt-4 flex items-center justify-between bg-white rounded-lg p-4 border border-blue-200">
            <div>
              <p className="text-sm text-gray-500">Total Transfer</p>
              <p className="text-xl font-bold text-[#0052CC]">Rp{formatPrice(order.total_amount)}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(order.total_amount.toString());
                toast.success('Amount copied!');
              }}
            >
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
          </div>
        </div>
      );
    }

    if (order.payment.type === 'cod') {
      return (
        <div className="bg-green-50 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Cash on Delivery</h4>
              <p className="text-sm text-gray-600 mt-1">
                Siapkan uang tunai sebesar <span className="font-bold text-green-700">Rp{formatPrice(order.total_amount)}</span> saat pesanan tiba.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#0052CC] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Order not found</h2>
          <Link to="/">
            <Button className="bg-[#0052CC] hover:bg-[#0043A8]">
              {t('back_to_home')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="order-confirmation-page">
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-500">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">
            {t('order_success')}
          </h1>
          <p className="text-gray-500 mt-2">Terima kasih atas pesanan Anda</p>
        </div>

        {/* Order Number */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6" data-testid="order-details">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{t('order_number')}</p>
              <p className="text-lg font-mono font-bold text-gray-900" data-testid="order-number">
                {order.order_number}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={copyOrderNumber}>
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
          </div>

          <div className="mt-4 flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
            <Clock className="w-4 h-4" />
            <span>Selesaikan pembayaran dalam 24 jam</span>
          </div>
        </div>

        {/* Payment Instructions */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
          <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">
            Instruksi Pembayaran
          </h3>
          {getPaymentInstructions()}
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
          <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">
            Ringkasan Pesanan
          </h3>

          <div className="space-y-3">
            {order.items.map((item, index) => (
              <div key={index} className="flex gap-3">
                <img
                  src={item.img_url || 'https://via.placeholder.com/48'}
                  alt={item.product_name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                  <p className="text-xs text-gray-500">{item.quantity}x Rp{formatPrice(item.price)}</p>
                </div>
                <p className="text-sm font-medium text-gray-900">
                  Rp{formatPrice(item.price * item.quantity)}
                </p>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 mt-4 pt-4">
            <div className="flex justify-between">
              <span className="font-medium text-gray-900">{t('order_total')}</span>
              <span className="text-xl font-bold text-[#0052CC]" data-testid="order-total">
                Rp{formatPrice(order.total_amount)}
              </span>
            </div>
          </div>
        </div>

        {/* Shipping Address */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-8">
          <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">
            Alamat Pengiriman
          </h3>
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-900">{order.address.name}</p>
            <p>{order.address.phone}</p>
            <p className="mt-2">{order.address.address}</p>
            <p>{order.address.district}, {order.address.city}</p>
            {order.address.notes && (
              <p className="text-gray-500 mt-2">Catatan: {order.address.notes}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/" className="flex-1">
            <Button variant="outline" className="w-full" data-testid="back-home-btn">
              {t('back_to_home')}
            </Button>
          </Link>
          <Button className="flex-1 bg-[#0052CC] hover:bg-[#0043A8]">
            Lacak Pesanan
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
